import type { NoiDungNhieuKenh } from '@qlk/shared';
import { Link } from 'react-router-dom';

import { EmptyState, Nut, Skeleton, soNguyen } from '../../../../ui/index.js';
import { gonSo, phanTram, thoiLuong } from '../dinhDang.js';
import { KhoiGop, ThanhNgang } from './KhoiGop.js';
import { ThanhBonO } from './ThanhBonO.js';

/**
 * Tab "Nội dung" của trang Hiệu suất.
 *
 * Ba khối: thanh bốn ô, hàng hai khung (phễu lượt hiển thị · loại nội dung),
 * rồi bảng "Video hàng đầu".
 */

/** Màu của ba loại nội dung — token, không mã màu gõ tay. */
const MAU_LOAI: Record<string, string> = {
  videoOnDemand: 'var(--color-do-xem)',
  shorts: 'var(--color-do-noi-dung)',
  liveStream: 'var(--color-do-tien)',
};

export interface TabNoiDungProps {
  du: NoiDungNhieuKenh | undefined;
  dangTai: boolean;
  loi: string | null;
  maKy: string;
  xemTatCa: boolean;
  onXemTatCa: () => void;
}

export function TabNoiDung({ du, dangTai, loi, maKy, xemTatCa, onXemTatCa }: TabNoiDungProps) {
  if (loi !== null) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState loi title="Không tải được số liệu nội dung" description={loi} />
      </div>
    );
  }

  const coSoLieu = du?.soKenhCoSoLieu ?? 0;
  const trongPhamVi = du?.soKenhTrongPhamVi ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <ThanhBonO o={du?.o ?? []} dangTai={dangTai} />

      <div className="grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-[3fr_2fr]">
        <KhoiGop
          tieuDe="Từ lượt hiển thị đến thời gian xem"
          soKenhCoSoLieu={du?.pheu?.soKenh ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={du?.pheu === null || du?.pheu === undefined}
          {...(du?.pheu && !du.pheu.duPhu
            ? { ghiChu: `Lượt hiển thị chỉ có từ ${du.pheu.tuNgay}` }
            : {})}
        >
          {du?.pheu && <Pheu pheu={du.pheu} />}
        </KhoiGop>

        <KhoiGop
          tieuDe="Lượt xem theo loại nội dung"
          soKenhCoSoLieu={coSoLieu}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={(du?.theoLoaiNoiDung ?? []).length === 0}
        >
          <LoaiNoiDung dong={du?.theoLoaiNoiDung ?? []} />
        </KhoiGop>
      </div>

      <BangVideo
        du={du}
        dangTai={dangTai}
        maKy={maKy}
        xemTatCa={xemTatCa}
        onXemTatCa={onXemTatCa}
      />
    </div>
  );
}

/**
 * Phễu bốn bước nối tiếp.
 *
 * Xếp **dọc** chứ không bốn ô ngang: mỗi bước là một con số nhỏ hơn bước trước,
 * và bốn ô cạnh nhau không nói lên quan hệ đó. Mỗi bước có một thanh dài theo
 * tỷ lệ so với bước đầu, nên mắt thấy ngay chỗ rụng nhiều nhất.
 */
function Pheu({ pheu }: { pheu: NonNullable<NoiDungNhieuKenh['pheu']> }) {
  const buoc = [
    {
      nhan: 'Lượt hiển thị',
      so: gonSo(pheu.impressions),
      phu: null as string | null,
      ty: 100,
    },
    {
      nhan: 'CTR',
      so: phanTram(pheu.ctr),
      phu: `${gonSo(pheu.soClick)} lượt bấm`,
      ty: Math.min(pheu.ctr, 100),
    },
    {
      nhan: 'Lượt xem từ hiển thị',
      so: gonSo(pheu.viewsTuHienThi),
      phu: `${phanTram(pheu.pctViews)} tổng lượt xem`,
      ty: pheu.impressions > 0 ? (pheu.viewsTuHienThi / pheu.impressions) * 100 : 0,
    },
    {
      nhan: 'Giờ xem từ hiển thị',
      so: gonSo(pheu.gioXemTuHienThi),
      phu: `${phanTram(pheu.pctGioXem)} tổng giờ xem`,
      ty: pheu.impressions > 0 ? (pheu.gioXemTuHienThi / pheu.impressions) * 100 : 0,
    },
  ];

  return (
    <ol className="flex flex-col gap-4">
      {buoc.map((b) => (
        <li key={b.nhan}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-co-ban text-chu">{b.nhan}</span>
            <span className="shrink-0 whitespace-nowrap">
              <span className="so-lieu text-co-ban font-semibold text-chu">{b.so}</span>
              {b.phu !== null && (
                <span className="so-lieu ml-2 text-bang text-chu-mo">{b.phu}</span>
              )}
            </span>
          </div>
          <div className="mt-2">
            <ThanhNgang phanTram={b.ty} />
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Thanh xếp chồng ba loại + ba dòng phần trăm và lượt xem. */
function LoaiNoiDung({ dong }: { dong: NoiDungNhieuKenh['theoLoaiNoiDung'] }) {
  return (
    <div>
      {/* Một thanh, ba đoạn: tỷ trọng giữa ba loại đọc được trong một cái nhìn. */}
      <span aria-hidden="true" className="flex h-2.5 overflow-hidden rounded-full bg-neutral-nen">
        {dong.map((d) => (
          <span
            key={d.contentType}
            className="block h-2.5"
            style={{
              width: `${String(d.phanTram)}%`,
              background: MAU_LOAI[d.contentType] ?? 'var(--color-do-trong)',
            }}
          />
        ))}
      </span>

      <ul className="mt-4 flex flex-col gap-3">
        {dong.map((d) => (
          <li key={d.contentType} className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span
                aria-hidden="true"
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: MAU_LOAI[d.contentType] ?? 'var(--color-do-trong)' }}
              />
              <span className="truncate text-co-ban text-chu">{d.nhan}</span>
            </span>
            <span className="shrink-0 whitespace-nowrap">
              <span className="so-lieu text-bang text-chu-mo">{gonSo(d.views)}</span>
              <span className="so-lieu ml-2 text-co-ban font-semibold text-chu">
                {phanTram(d.phanTram)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Bảng "Video hàng đầu" — bảy cột, **cuộn ngang trong khung của chính nó**.
 *
 * `overflow-x-auto` trên khung bọc bảng, không trên trang: bảng rộng thì cuộn
 * trong khung, còn trang cuộn ngang thì menu và tiêu đề trôi theo (`docs/ui.md`
 * mục 15, và bài e2e 91).
 */
function BangVideo({
  du,
  dangTai,
  maKy,
  xemTatCa,
  onXemTatCa,
}: {
  du: NoiDungNhieuKenh | undefined;
  dangTai: boolean;
  maKy: string;
  xemTatCa: boolean;
  onXemTatCa: () => void;
}) {
  const dong = du?.videoHangDau ?? [];
  const conLai = (du?.tongVideo ?? 0) - dong.length;

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6" data-khoi="video-hang-dau">
      <h2 className="text-tieu-de-khoi font-semibold text-chu">Video hàng đầu</h2>
      <p className="mt-1 text-phu text-chu-mo">Xếp theo lượt xem trong kỳ.</p>

      {dangTai ? (
        <div className="mt-4">
          <Skeleton cao="18rem" />
        </div>
      ) : dong.length === 0 ? (
        <EmptyState title="Chưa có video nào có lượt xem trong kỳ này" />
      ) : (
        <>
          <div data-khoi="cuon-bang" className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-bang">
              <thead>
                <tr className="bg-header-bang">
                  <th scope="col" className="px-3 py-2 font-semibold text-chu-phu">
                    Video
                  </th>
                  <th scope="col" className="px-3 py-2 font-semibold text-chu-phu">
                    Kênh
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-chu-phu">
                    Lượt xem
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-chu-phu">
                    Giờ xem
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-chu-phu">
                    Thời lượng TB
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold text-chu-phu">
                    CTR
                  </th>
                </tr>
              </thead>
              <tbody>
                {dong.map((v) => (
                  <tr key={v.videoId} className="border-t border-vien-trong">
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-3">
                        {v.thumbnailUrl === null ? (
                          <span
                            aria-hidden="true"
                            className="h-[36px] w-[64px] shrink-0 rounded-o bg-neutral-nen"
                          />
                        ) : (
                          <img
                            src={v.thumbnailUrl}
                            alt=""
                            loading="lazy"
                            width={64}
                            height={36}
                            className="h-[36px] w-[64px] shrink-0 rounded-o object-cover"
                          />
                        )}
                        {/* Bấm tên video sang **trang kênh** giữ `?ky=`: app chưa
                            có trang riêng cho một video. */}
                        <Link
                          to={`/kenh/${v.channelId}?ky=${encodeURIComponent(maKy)}`}
                          title={v.title}
                          className="line-clamp-2 max-w-[22rem] text-chu hover:text-nhan hover:underline"
                        >
                          {v.title}
                        </Link>
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/kenh/${v.channelId}?ky=${encodeURIComponent(maKy)}`}
                        className="text-chu-phu hover:text-nhan hover:underline"
                      >
                        {v.channelName}
                      </Link>
                    </td>
                    <td className="so-lieu px-3 py-2 text-right font-semibold text-chu">
                      {gonSo(v.views)}
                    </td>
                    <td className="so-lieu px-3 py-2 text-right text-chu-phu">{gonSo(v.gioXem)}</td>
                    <td className="so-lieu px-3 py-2 text-right text-chu-phu">
                      {thoiLuong(v.averageViewDuration)}
                    </td>
                    {/* `null` = video chưa có lượt hiển thị ⇒ `—`, không `0%`. */}
                    <td className="so-lieu px-3 py-2 text-right text-chu-phu">
                      {v.ctr === null ? '—' : phanTram(v.ctr)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {conLai > 0 && !xemTatCa && (
            <div className="mt-4">
              <Nut kieu="phu" onClick={onXemTatCa}>
                Xem tất cả ({soNguyen(du?.tongVideo ?? 0)} video)
              </Nut>
            </div>
          )}
        </>
      )}
    </section>
  );
}
